/* iter-payments-master — Site-wide "payments paused" banner.
 *
 * Rendered at the App root so it overlays every route (Landing,
 * Bookstore, The Loom shell, Admin). Shows only when the Admin has
 * flipped `integrations_enabled.stripe = false` on the Pricing panel.
 *
 * Non-intrusive: sits at the top as a slim red strip. `pointer-events`
 * remain on the strip so the "×" dismiss button is clickable, but the
 * dismissal is per-session (sessionStorage) — flipping the flag back
 * ON server-side hides the banner site-wide instantly on next poll.
 */
import React, { useEffect, useState } from 'react';
import { AlertOctagon, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { usePaymentsStatus } from '@/lib/payments_status';

const SESSION_KEY = 'cnr:payment-downtime-banner-dismissed';

// Feb-2026 iter — admin/back-office prefixes where the payments
// downtime strip must NOT paint. The strip is site-wide informational
// for shoppers only.
const HIDE_ON_PREFIXES = ['/admin', '/bookstore/admin', '/editor'];

export default function PaymentDowntimeBanner() {
  const location = useLocation();
  const { ready, enabled, reason } = usePaymentsStatus();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') setDismissed(true);
    } catch { /* private mode */ }
  }, []);

  // Reset dismissal state when the server toggles the flag ON so a
  // fresh downtime shows the banner again after the previous outage.
  useEffect(() => {
    if (ready && enabled) {
      try { window.sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
      setDismissed(false);
    }
  }, [ready, enabled]);

  if (!ready || enabled || dismissed) return null;
  const path = location?.pathname || '';
  if (HIDE_ON_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))) {
    return null;
  }

  const dismiss = () => {
    try { window.sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[70] w-full border-b border-rose-800/60 bg-rose-700 text-white shadow-lg"
      role="alert"
      aria-live="polite"
      data-testid="payment-downtime-banner"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2 text-[12.5px] sm:text-[13px]">
        <AlertOctagon className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1 leading-snug">
          <span className="font-semibold uppercase tracking-wider">
            Card processing paused ·
          </span>{' '}
          <span data-testid="payment-downtime-banner-reason">
            {reason || 'The store is temporarily not accepting payments. Please try again shortly.'}
          </span>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="payment-downtime-banner-dismiss"
          aria-label="Dismiss payment downtime banner for this session"
          className="rounded p-1 text-white/90 transition hover:bg-white/10 hover:text-white"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
